import { useCallback, useEffect, useRef, useState } from 'react'
import { isAuthError } from 'workers-common'
import { executeChat } from '../services/chat-orchestrator.js'

export const useChatWithSteps = (options: any = {}) => {
  const {
    api,
    onAuthError,
    peerService,
    conversationId,
    host, // { type, roleId?, botConfig?, nameMap? }
    participants, // [{ roleId, mentionName, roleType, displayName, description }]
  } = options

  const [messages, setMessages] = useState<any[]>([])
  const [isThinking, setIsThinking] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(true)
  const [currentStep, setCurrentStep] = useState<any>(null)

  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId

  // Track latest messages for sendMessage without re-fetching
  const messagesRef = useRef(messages)
  messagesRef.current = messages

  // Load conversation history when conversationId changes
  useEffect(() => {
    if (!api || !conversationId) {
      setIsLoadingHistory(false)
      return
    }
    setIsLoadingHistory(true)
    setMessages([])
    ;(async () => {
      try {
        const msgs = await api.getMessages(conversationId)
        if (msgs?.length > 0) setMessages(msgs.filter((m: any) => m.content))
      } catch (error) {
        console.error('[useChatWithSteps] Failed to load history:', error)
      } finally {
        setIsLoadingHistory(false)
      }
    })()
  }, [api, conversationId])

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isThinking || !peerService?.chatEngine || !conversationId || !host)
        return
      const trimmed = content.trim()
      const convId = conversationIdRef.current

      setIsThinking(true)

      try {
        await executeChat(trimmed, {
          chatEngine: peerService.chatEngine,
          api,
          conversationId: convId,
          host,
          participants: participants || [],
          messages: [...messagesRef.current],
          save: async (msg: any) => {
            setMessages(prev => [...prev, msg])
            await api.saveMessages(convId, [msg])
          },
          onStep: setCurrentStep,
        })
      } catch (error: any) {
        console.error('[useChatWithSteps] Failed:', error)
        if (isAuthError(error)) onAuthError?.(error)
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: `Error: ${error.message || 'Failed to send message'}`,
            timestamp: Date.now(),
            isError: true,
          },
        ])
      } finally {
        setIsThinking(false)
        setCurrentStep(null)
      }
    },
    [api, isThinking, peerService, conversationId, host, participants, onAuthError],
  )

  const appendMessage = useCallback((msg: any) => {
    setMessages(prev => {
      const taskId = msg.metadata?.taskId
      if (!taskId) return [...prev, msg]
      const idx = prev.findIndex(
        (m: any) =>
          m.metadata?.taskId === taskId &&
          (m.metadata?.type === 'progress' || m.metadata?.type === 'chat_pending'),
      )
      if (idx >= 0) {
        const updated = [...prev]
        updated[idx] = msg
        return updated
      }
      return [...prev, msg]
    })
  }, [])

  const clearMessages = useCallback(() => setMessages([]), [])

  return {
    messages,
    isThinking,
    isLoadingHistory,
    currentStep,
    sendMessage,
    appendMessage,
    clearMessages,
  }
}
